import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import MainLayout from "./layout/MainLayout";
import { useTheme } from "../../ThemeContext";
import { UsecaseStructureProvider } from "./components/UsecaseSelector/UsecaseStructureContext";
import LeftPanel from "./components/LeftPanel";

const CallAutomationV2 = () => {
  const { themeMode, currentTheme } = useTheme();
  
  // --- Router Hooks ---
  const location = useLocation();
  const navigate = useNavigate();

  // --- Initialize state from location.state or defaults ---
  const initialState = location.state || {};
  const [selectedDates, setSelectedDates] = useState(initialState.selectedDates || []);
  const [selectedStartTime, setSelectedStartTime] = useState(initialState.selectedStartTime || "");
  const [selectedEndTime, setSelectedEndTime] = useState(initialState.selectedEndTime || "");
  const [branch, setBranch] = useState(initialState.branch || "");
  const [usecase, setUsecase] = useState(initialState.usecase || "");
  const [subusecase, setSubusecase] = useState(initialState.subusecase || "");
  const [selectionMode, setSelectionMode] = useState(initialState.selectionMode || 0); 

  // --- Effect to potentially update state if location.state changes (e.g., browser back/forward with state)
  // Optional, but can help keep state consistent if navigating with state via browser buttons
  useEffect(() => {
    const newState = location.state || {};
    setSelectedDates(newState.selectedDates || []);
    setSelectedStartTime(newState.selectedStartTime || "");
    setSelectedEndTime(newState.selectedEndTime || "");
    setBranch(newState.branch || "");
    setUsecase(newState.usecase || "");
    setSubusecase(newState.subusecase || "");
    setSelectionMode(newState.selectionMode || 0);
  }, [location.state]);

  // --- Handlers --- (Simplified, state is managed locally, passed on navigate)
  const handleDateChange = (dates) => setSelectedDates(dates);
  const handleStartTimeChange = (time) => setSelectedStartTime(time);
  const handleEndTimeChange = (time) => setSelectedEndTime(time);

  const handleBranchChange = (value) => {
    setBranch(value);
    setUsecase(""); 
    setSubusecase(""); 
  };

  const handleUsecaseChange = (value) => {
    setUsecase(value);
    setSubusecase(""); 
  };

  const handleSubusecaseChange = (value) => setSubusecase(value);

  const handleReset = () => {
    const clearedState = { 
        selectedDates: [], 
        selectedStartTime: "", 
        selectedEndTime: "", 
        branch: "", 
        usecase: "", 
        subusecase: "",
        selectionMode: 0 
    };
    // Reset local state
    setSelectedDates(clearedState.selectedDates);
    setSelectedStartTime(clearedState.selectedStartTime);
    setSelectedEndTime(clearedState.selectedEndTime);
    setBranch(clearedState.branch);
    setUsecase(clearedState.usecase);
    setSubusecase(clearedState.subusecase);
    setSelectionMode(clearedState.selectionMode);
    // Navigate to base route with cleared state if not already there
    if (location.pathname !== '/call-automation-v2') {
        navigate('/call-automation-v2', { state: clearedState, replace: true });
    }
  };

  const handleSelectionModeChange = (value) => {
    setSelectionMode(value);
    setSelectedDates([]); 
  };

  // --- Submit Handler: Navigate with state object ---
  const handleSubmit = () => {
    const currentState = {
      selectedDates,
      selectedStartTime,
      selectedEndTime,
      branch,
      usecase,
      subusecase,
      selectionMode
    };
    // Navigate to the next step with the state
    navigate('/call-automation-v2/question-selection', { state: currentState });
  };

  return (
    <UsecaseStructureProvider>
      <MainLayout
        leftContent={
          <LeftPanel
            themeMode={themeMode}
            currentTheme={currentTheme}
            selectedDates={selectedDates}
            selectedStartTime={selectedStartTime}
            selectedEndTime={selectedEndTime}
            branch={branch}
            usecase={usecase}
            subusecase={subusecase}
            selectionMode={selectionMode}
            onDateChange={handleDateChange} 
            onStartTimeChange={handleStartTimeChange}
            onEndTimeChange={handleEndTimeChange}
            onBranchChange={handleBranchChange}
            onUsecaseChange={handleUsecaseChange}
            onSubusecaseChange={handleSubusecaseChange}
            onReset={handleReset}
            onSubmit={handleSubmit} 
            onSelectionModeChange={handleSelectionModeChange}
          />
        }
        rightContent={
          // Pass state down via context for child routes to access easily
          <Outlet context={{ themeMode, currentTheme, formState: location.state || {} }} /> 
        }
      />
    </UsecaseStructureProvider>
  );
};

export default CallAutomationV2;
